import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import BlockIcon from "@material-ui/icons/Block"
import EmailIcon from "@material-ui/icons/Email"
import PersonAddIcon from "@material-ui/icons/PersonAdd"
import SaveIcon from "@material-ui/icons/Save"
import VpnKeyIcon from "@material-ui/icons/VpnKey"
import React, { useEffect, useState } from "react"
import {
    CredenciaisUsuario,
    desativarUsuario,
    enviarEmailBoasVindas,
    listarUsuarios,
    NovoUsuarioPainel,
    obterCredenciais,
    PERFIS_USUARIO,
    salvarUsuario,
    UsuarioPainel,
} from "../../../services/usuariosPainel"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"
import { useSnackbar } from "../../../utils/notificacoes"

const formularioVazio: NovoUsuarioPainel = {
    nome: "",
    email: "",
    telefone: "",
    perfil: "",
    senha: "",
    receberEmailPedidoExpirado: false,
    receberEmailPedidoRealizado: false,
    receberEmailPedidoCancelado: false,
    reenvioImediatoCodigoAutenticacao: false,
}

const rotuloPerfil = (valor: string) => PERFIS_USUARIO.find(p => p.valor === valor)?.rotulo ?? valor

/**
 * CRUD dos usuários do painel (entidade Fornecedor no backend).
 *
 * Réplica da tela Tapestry UsuariosFornecedor/NovoUsuarioFornecedor: lista, cria/edita, desativa
 * (exclusão lógica), envia e-mail de boas-vindas e mostra as credenciais. Restrita à equipe
 * Cliente Fiel — o backend também confere o perfil, não só o menu.
 */
export function UsuariosPainelForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [usuarios, setUsuarios] = useState<UsuarioPainel[]>([])
    const [formulario, setFormulario] = useState<NovoUsuarioPainel>(formularioVazio)
    const [editandoId, setEditandoId] = useState<number | null>(null)
    const [salvando, setSalvando] = useState(false)
    const [credenciais, setCredenciais] = useState<CredenciaisUsuario | null>(null)

    const alertStart = (msg: string, tipo: "success" | "error" | "info" | "warning") =>
        enqueueSnackbar(msg, { variant: tipo })

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await listarUsuarios(autorizacao)
            if ("retornoErro" in res) alertStart(res.mensagem || "Erro ao carregar", "error")
            else setUsuarios(res)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const limparFormulario = () => {
        setFormulario(formularioVazio)
        setEditandoId(null)
    }

    const editar = (usuario: UsuarioPainel) => {
        setEditandoId(usuario.id)
        setFormulario({
            id: usuario.id,
            nome: usuario.nome ?? "",
            email: usuario.email ?? "",
            telefone: usuario.telefone ?? "",
            perfil: usuario.perfil ?? "",
            senha: "", // vazio mantém a atual
            receberEmailPedidoExpirado: usuario.receberEmailPedidoExpirado,
            receberEmailPedidoRealizado: usuario.receberEmailPedidoRealizado,
            receberEmailPedidoCancelado: usuario.receberEmailPedidoCancelado,
            reenvioImediatoCodigoAutenticacao: usuario.reenvioImediatoCodigoAutenticacao,
        })
        window.scrollTo({ top: 0, behavior: "smooth" })
    }

    const alterarTexto =
        (campo: "nome" | "email" | "telefone" | "senha") =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const valor = e.target.value
            setFormulario(prev => ({ ...prev, [campo]: valor }))
        }

    const alterarFlag =
        (campo: keyof NovoUsuarioPainel) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const marcado = e.target.checked
            setFormulario(prev => ({ ...prev, [campo]: marcado }))
        }

    const handleSalvar = async () => {
        if (!formulario.nome.trim() || !formulario.email.trim() || !formulario.perfil) {
            alertStart("Nome, e-mail e perfil são obrigatórios.", "error")
            return
        }
        if (!editandoId && !formulario.senha?.trim()) {
            alertStart("A senha é obrigatória ao criar um usuário.", "error")
            return
        }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setSalvando(true)
        try {
            const res = await salvarUsuario(autorizacao, formulario)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao salvar usuário", "error")
            } else {
                alertStart(editandoId ? "Usuário atualizado." : "Usuário cadastrado.", "success")
                limparFormulario()
                carregar()
            }
        } finally {
            setSalvando(false)
        }
    }

    const handleDesativar = async (usuario: UsuarioPainel) => {
        if (!window.confirm(`Desativar o usuário "${usuario.nome}"?`)) return
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await desativarUsuario(autorizacao, usuario.id)
            if ("retornoErro" in res) alertStart(res.mensagem || "Erro ao desativar", "error")
            else {
                alertStart("Usuário desativado.", "success")
                carregar()
            }
        } finally {
            setLoading(false)
        }
    }

    const handleEmailBoasVindas = async (usuario: UsuarioPainel) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await enviarEmailBoasVindas(autorizacao, usuario.id)
            if ("retornoErro" in res) alertStart(res.mensagem || "Erro ao enviar e-mail", "error")
            else alertStart("E-mail de boas-vindas enviado.", "success")
        } finally {
            setLoading(false)
        }
    }

    const handleCredenciais = async (usuario: UsuarioPainel) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await obterCredenciais(autorizacao, usuario.id)
            if ("retornoErro" in res)
                alertStart(res.mensagem || "Erro ao obter credenciais", "error")
            else setCredenciais(res)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Usuários
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            {editandoId ? "Editar usuário" : "Novo usuário"}
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nome"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.nome}
                                    onChange={alterarTexto("nome")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="E-mail"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.email}
                                    onChange={alterarTexto("email")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Telefone"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.telefone}
                                    onChange={alterarTexto("telefone")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel>Perfil</InputLabel>
                                    <Select
                                        label="Perfil"
                                        value={formulario.perfil}
                                        onChange={e =>
                                            setFormulario(prev => ({
                                                ...prev,
                                                perfil: String(e.target.value),
                                            }))
                                        }
                                    >
                                        {PERFIS_USUARIO.map(p => (
                                            <MenuItem key={p.valor} value={p.valor}>
                                                {p.rotulo}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label={editandoId ? "Nova senha (opcional)" : "Senha"}
                                    type="password"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.senha}
                                    onChange={alterarTexto("senha")}
                                    helperText={
                                        editandoId ? "Deixe em branco para manter a atual." : ""
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    Notificações por e-mail
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formulario.receberEmailPedidoRealizado}
                                            onChange={alterarFlag("receberEmailPedidoRealizado")}
                                            color="primary"
                                        />
                                    }
                                    label="Pedido realizado"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formulario.receberEmailPedidoCancelado}
                                            onChange={alterarFlag("receberEmailPedidoCancelado")}
                                            color="primary"
                                        />
                                    }
                                    label="Pedido cancelado"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formulario.receberEmailPedidoExpirado}
                                            onChange={alterarFlag("receberEmailPedidoExpirado")}
                                            color="primary"
                                        />
                                    }
                                    label="Pedido expirado"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formulario.reenvioImediatoCodigoAutenticacao}
                                            onChange={alterarFlag(
                                                "reenvioImediatoCodigoAutenticacao"
                                            )}
                                            color="primary"
                                        />
                                    }
                                    label="Reenvio imediato do código de autenticação"
                                />
                            </Grid>
                        </Grid>

                        <Box display="flex" justifyContent="flex-end" mt={2} gridGap={8}>
                            {editandoId && (
                                <Button variant="outlined" onClick={limparFormulario}>
                                    Cancelar edição
                                </Button>
                            )}
                            <Button
                                className={classes.saveButton}
                                variant="contained"
                                startIcon={editandoId ? <SaveIcon /> : <PersonAddIcon />}
                                onClick={handleSalvar}
                                disabled={salvando}
                            >
                                {salvando ? "Salvando..." : editandoId ? "Salvar" : "Cadastrar"}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Usuários cadastrados
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {usuarios.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                                Nenhum usuário cadastrado.
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>E-mail</TableCell>
                                        <TableCell>Perfil</TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {usuarios.map(usuario => (
                                        <TableRow key={usuario.id}>
                                            <TableCell>{usuario.nome}</TableCell>
                                            <TableCell>{usuario.email}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={rotuloPerfil(usuario.perfil)}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    title="Editar"
                                                    onClick={() => editar(usuario)}
                                                >
                                                    <SaveIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    title="Ver credenciais"
                                                    onClick={() => handleCredenciais(usuario)}
                                                >
                                                    <VpnKeyIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    title="Enviar e-mail de boas-vindas"
                                                    onClick={() => handleEmailBoasVindas(usuario)}
                                                >
                                                    <EmailIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    title="Desativar"
                                                    onClick={() => handleDesativar(usuario)}
                                                >
                                                    <BlockIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                </Paper>
            </Box>

            <Dialog
                open={credenciais !== null}
                onClose={() => setCredenciais(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Credenciais de integração</DialogTitle>
                <DialogContent>
                    <TextField
                        label="E-mail"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="dense"
                        value={credenciais?.email ?? ""}
                        InputProps={{ readOnly: true }}
                        onFocus={e => e.target.select()}
                    />
                    <TextField
                        label="Senha"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="dense"
                        value={credenciais?.senha ?? ""}
                        InputProps={{ readOnly: true }}
                        onFocus={e => e.target.select()}
                    />
                    <TextField
                        label="Token de integração (Basic)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="dense"
                        multiline
                        value={credenciais?.tokenIntegracao ?? ""}
                        InputProps={{ readOnly: true }}
                        onFocus={e => e.target.select()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCredenciais(null)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
